import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Zap, Shield, ArrowRight, Sparkles, Moon, Globe, Star } from './Icons';
import Footer from './Footer';
import { trackEvent } from '../utils/analytics';

const Pricing = ({ onSelectPlan }) => {
  const [billingInterval, setBillingInterval] = useState('monthly');

  // Plan definitions
  const plans = [
    {
      id: 'luna',
      name: 'Luna',
      icon: Moon,
      description: 'Perfect for trying out AI grading',
      monthlyPrice: 0,
      annualPrice: 0,
      students: 3,
      workspaces: 1,
      addOnRate: null,
      features: [
        '3 students per period',
        '1 assignment workspace',
        'Basic AI grading',
        'Standard processing'
      ],
      cta: 'Get Started Free',
      popular: false,
      accent: 'zinc'
    },
    {
      id: 'mercury',
      name: 'Mercury',
      icon: Sparkles,
      description: 'For individual educators',
      monthlyPrice: 19,
      annualPrice: 193.80, // 15% off
      students: 25,
      workspaces: 1,
      addOnRate: 0.80,
      features: [
        '25 students per period',
        '1 assignment workspace',
        'Priority AI processing',
        'Excel export',
        'Add-on: $0.80/student'
      ],
      cta: 'Start with Mercury',
      popular: false,
      accent: 'orange'
    },
    {
      id: 'neptune',
      name: 'Neptune',
      icon: Globe,
      description: 'For growing classrooms',
      monthlyPrice: 39,
      annualPrice: 397.80, // 15% off
      students: 60,
      workspaces: 2,
      addOnRate: 0.50,
      features: [
        '60 students per period',
        '2 assignment workspaces',
        'Priority AI processing',
        'Excel export',
        'Advanced analytics',
        'Add-on: $0.50/student'
      ],
      cta: 'Start with Neptune',
      popular: true,
      accent: 'blue'
    },
    {
      id: 'jupiter',
      name: 'Jupiter',
      icon: Star,
      description: 'For departments & power users',
      monthlyPrice: 69,
      annualPrice: 703.80, // 15% off
      students: 130,
      workspaces: 5,
      addOnRate: 0.30,
      features: [
        '130 students per period',
        '5 assignment workspaces',
        'Priority AI processing',
        'Excel export',
        'Advanced analytics',
        'API access',
        'Add-on: $0.30/student'
      ],
      cta: 'Start with Jupiter',
      popular: false,
      accent: 'purple'
    }
  ];

  const getPrice = (plan) => {
    if (plan.monthlyPrice === 0) return 0;
    return billingInterval === 'annual'
      ? (plan.annualPrice / 12).toFixed(2)
      : plan.monthlyPrice;
  };

  const getAnnualSavings = (plan) => {
    if (plan.monthlyPrice === 0) return 0;
    return Math.round((plan.monthlyPrice * 12 - plan.annualPrice));
  };

  const handleSelectPlan = (planId) => {
    trackEvent('select_plan_click', { plan: planId, billing: billingInterval });
    if (planId === 'luna') {
      onSelectPlan('Luna');
    } else {
      // Pass both plan and billing interval to parent
      onSelectPlan(planId.charAt(0).toUpperCase() + planId.slice(1), billingInterval);
    }
  };

  const getAccentClasses = (accent, popular) => {
    if (popular) {
      return {
        border: 'ring-2 ring-black relative z-10',
        bg: 'bg-white',
        iconBg: 'bg-zinc-100',
        iconText: 'text-black',
        button: 'bg-black text-white hover:bg-zinc-800 shadow-sm',
        badge: 'bg-black text-white',
        highlight: 'text-black'
      };
    }

    return {
      border: 'border border-zinc-200 hover:border-zinc-300',
      bg: 'bg-white',
      iconBg: 'bg-zinc-50',
      iconText: 'text-zinc-900',
      button: 'bg-white border border-zinc-200 text-zinc-900 hover:bg-zinc-50',
      badge: 'bg-zinc-900',
      highlight: 'text-zinc-500'
    };
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900 font-sans selection:bg-zinc-900 selection:text-white flex flex-col">
      <nav className="w-full p-6 flex justify-start items-center max-w-7xl mx-auto">
        <Link
          to="/"
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
        >
          <div className="w-6 h-6 bg-white border-2 border-black rounded-sm flex items-center justify-center">
            <Check className="w-4 h-4 text-black" strokeWidth={3} />
          </div>
          <span className="font-bold text-lg tracking-tight">GradeMind.ai</span>
        </Link>
      </nav>

      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12">
        <div className="text-center max-w-3xl mb-10">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-zinc-900">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-zinc-500 mb-8">
            Choose the plan that fits your classroom. Scale with add-ons as needed.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 bg-zinc-100 p-1 rounded-full">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${billingInterval === 'monthly'
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('annual')}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-2 ${billingInterval === 'annual'
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
                }`}
            >
              Annual
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">
                -15%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl w-full">
          {plans.map((plan) => {
            const styles = getAccentClasses(plan.accent, plan.popular);
            const Icon = plan.icon;
            const price = getPrice(plan);
            const savings = getAnnualSavings(plan);

            return (
              <div
                key={plan.id}
                className={`flex flex-col p-6 rounded-2xl transition-all ${styles.border} ${styles.bg} ${plan.popular ? 'transform lg:-translate-y-2 shadow-xl' : ''
                  }`}
              >
                {plan.popular && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${styles.badge} text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider`}>
                    Most Popular
                  </div>
                )}

                <div className="mb-4">
                  <div className={`w-10 h-10 ${styles.iconBg} rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className={`w-5 h-5 ${styles.iconText}`} />
                  </div>
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <p className="text-sm text-zinc-500 mt-1">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">
                      ${price === 0 ? '0' : price}
                    </span>
                    {plan.monthlyPrice > 0 && (
                      <span className="text-zinc-500">/mo</span>
                    )}
                  </div>
                  {billingInterval === 'annual' && plan.monthlyPrice > 0 && (
                    <p className="text-sm text-green-600 mt-1">
                      Save ${savings}/year
                    </p>
                  )}
                  {billingInterval === 'annual' && plan.monthlyPrice > 0 && (
                    <p className="text-xs text-zinc-400 mt-0.5">
                      Billed ${plan.annualPrice.toFixed(2)} annually
                    </p>
                  )}
                  {plan.monthlyPrice === 0 && (
                    <p className="text-sm text-zinc-500 mt-1">Free forever</p>
                  )}
                </div>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  className={`w-full py-3 rounded-lg font-medium transition-all mb-6 flex items-center justify-center gap-2 ${styles.button}`}
                >
                  {plan.cta}
                  {plan.monthlyPrice > 0 && <ArrowRight className="w-4 h-4" />}
                </button>

                <div className="space-y-3 flex-1">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-sm text-zinc-700">
                      <Check className="w-4 h-4 text-zinc-900 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Student & Workspace info */}
                <div className="mt-6 pt-4 border-t border-zinc-100">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Students</span>
                    <span className="font-medium">{plan.students}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-zinc-500">Workspaces</span>
                    <span className="font-medium">{plan.workspaces}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Institution CTA */}
        <div className="mt-12 w-full max-w-2xl mx-auto">
          <div className="p-6 rounded-2xl border border-zinc-200 bg-gradient-to-r from-zinc-50 to-white text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Shield className="w-6 h-6 text-zinc-700" />
              <h3 className="text-lg font-bold">Need an Institution Plan?</h3>
            </div>
            <p className="text-zinc-500 mb-4">
              For departments and schools with custom requirements, SSO, and dedicated support.
            </p>
            <button
              onClick={() => onSelectPlan('Institution')}
              className="px-6 py-2 rounded-lg border border-zinc-200 text-zinc-900 font-medium hover:bg-zinc-50 transition-colors"
            >
              Contact Sales
            </button>
          </div>
        </div>

        <p className="mt-8 text-sm text-zinc-400 text-center">
          Student limits reset each billing period. Add-on rates are the same for monthly and annual plans.
          <br />
          14-day money-back guarantee. Cancel anytime.
        </p>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Pricing;
